import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { EntityType, Entity, AttributeDataType, QueryFilter } from '../types';
import type { ColumnDef } from '../config/viewsConfig';
import { labelForExtractColumn } from '../config/viewsConfig';
import { loadModalAttributeContext } from '../utils/discoverAttributes';
import { limsClient } from '../api/limsClient';
import './Modal.css';
import './EntityFilters.css';

const EMPTY_ENTITIES: Entity[] = [];
const EMPTY_COLUMNS: ColumnDef[] = [];

interface EntityFiltersProps {
  entityType: EntityType;
  filters?: QueryFilter[];
  onFiltersChange: (filters: QueryFilter[]) => void;
  /** Entities for JSON key discovery fallback when API returns no rows. */
  entities?: Entity[];
  /** Merged with discovery queries (e.g. lab filters) so JSON key sampling matches the list view. */
  extraFilters?: QueryFilter[];
  /**
   * Curated view columns. Discovered JSON paths that match an extract column
   * use that column's short label (e.g. Mn instead of worksheet_data.report_export_mn.value).
   */
  knownColumns?: ColumnDef[];
}

interface FilterableAttribute {
  name: string;
  attribute_name: string;
  data_type: AttributeDataType;
  groupId: string;
  groupLabel: string;
  /** For cross-entity attrs: the entity type to fetch suggestions from. */
  refEntityType?: EntityType;
  json_path?: string;
  /** Whole JSON blob IS NULL / IS NOT NULL only (no json_path). */
  wholeJsonColumn?: boolean;
}

interface AttrGroup {
  id: string;
  label: string;
  items: FilterableAttribute[];
}

type OperatorOption = NonNullable<QueryFilter['operator']> | 'BETWEEN' | 'contains';

const OPERATORS: Record<AttributeDataType, OperatorOption[]> = {
  text: ['=', '!=', 'contains', 'IN', 'IS NULL', 'IS NOT NULL'],
  number: ['=', '!=', '>', '>=', '<', '<=', 'IN', 'IS NULL', 'IS NOT NULL'],
  boolean: ['=', '!='],
  date: ['=', '>', '>=', '<', '<=', 'BETWEEN', 'IS NULL', 'IS NOT NULL'],
  json: ['=', '!=', 'IS NULL', 'IS NOT NULL'],
};

const OPERATOR_DISPLAY: Record<string, string> = {
  'contains': 'contains',
  'BETWEEN': 'between',
};

/** Operators for filtering on the entire JSON column (not recommended: = / != on raw blob). */
const WHOLE_JSON_COLUMN_OPERATORS: OperatorOption[] = ['IS NULL', 'IS NOT NULL'];

function filterableAttrMatchesSearch(attr: FilterableAttribute, searchRaw: string): boolean {
  const search = searchRaw.trim().toLowerCase();
  if (!search) return true;
  if (attr.name.toLowerCase().includes(search)) return true;
  if (attr.attribute_name.toLowerCase().includes(search)) return true;
  if (attr.groupLabel.toLowerCase().includes(search)) return true;
  if (attr.json_path) {
    if (attr.json_path.toLowerCase().includes(search)) return true;
    const composite = `${attr.attribute_name}.${attr.json_path}`.toLowerCase();
    if (composite.includes(search)) return true;
  }
  return false;
}

const displayOperator = (op: string): string =>
  op === 'ILIKE' ? 'contains' : op;

function findAttrMatch(f: QueryFilter, attrs: FilterableAttribute[]): FilterableAttribute | undefined {
  return attrs.find(a =>
    a.attribute_name === f.attribute_name &&
    (a.json_path ?? null) === (f.json_path ?? null),
  );
}

const formatFilterDisplay = (
  f: QueryFilter,
  attrs: FilterableAttribute[] = [],
  knownColumns: ColumnDef[] = EMPTY_COLUMNS,
): string => {
  const match = findAttrMatch(f, attrs);
  const knownLabel = f.json_path
    ? labelForExtractColumn(f.attribute_name, f.json_path, knownColumns)
    : undefined;
  const name = match?.name
    ?? knownLabel
    ?? (f.json_path ? `${f.attribute_name}.${f.json_path}` : f.attribute_name);
  if (Array.isArray(f.or) && f.or.length > 0) {
    const clauses = f.or.map(c => {
      if (c.operator === 'IS NULL' || c.operator === 'IS NOT NULL') return c.operator;
      const v = Array.isArray(c.value) ? c.value.join(', ') : String(c.value ?? '');
      return `${displayOperator(c.operator ?? '=')} ${v}`;
    });
    return `${name} ${clauses.join(' OR ')}`;
  }
  if (f.operator === 'IS NULL' || f.operator === 'IS NOT NULL') return `${name} ${f.operator}`;
  const val = Array.isArray(f.value) ? f.value.join(', ') : String(f.value ?? '');
  return `${name} ${displayOperator(f.operator ?? '=')} ${val}`;
};


function jsonLeafName(attrName: string, path: string, knownColumns: ColumnDef[]): string {
  return labelForExtractColumn(attrName, path, knownColumns) ?? `${attrName}.${path}`;
}

export const EntityFilters: React.FC<EntityFiltersProps> = ({
  entityType,
  filters: externalFilters,
  onFiltersChange,
  entities: sampleEntities = EMPTY_ENTITIES,
  extraFilters = [],
  knownColumns = EMPTY_COLUMNS,
}) => {
  const [appliedFilters, setAppliedFilters] = useState<QueryFilter[]>(externalFilters || []);
  const [open, setOpen] = useState(false);
  const [allAttrs, setAllAttrs] = useState<FilterableAttribute[]>([]);
  const [attrSearch, setAttrSearch] = useState('');
  const [selectedAttr, setSelectedAttr] = useState<FilterableAttribute | null>(null);
  const [operator, setOperator] = useState<OperatorOption>('=');
  const [value, setValue] = useState<string>('');
  const [inPills, setInPills] = useState<string[]>([]);
  const [betweenFrom, setBetweenFrom] = useState('');
  const [betweenTo, setBetweenTo] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const pendingEditRef = useRef<QueryFilter | null>(null);

  useEffect(() => {
    const incoming = externalFilters || [];
    if (JSON.stringify(incoming) !== JSON.stringify(appliedFilters)) {
      setAppliedFilters(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFilters]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { directAttrs, fkGroups, jsonAttrNames, discoveredJson } = await loadModalAttributeContext(
          entityType,
          {
            extraFilters,
            fallbackEntities: sampleEntities,
            cancelled: () => cancelled,
          },
        );
        if (cancelled) return;

        const result: FilterableAttribute[] = [];
        result.push({
          name: 'id', attribute_name: 'id', data_type: 'number',
          groupId: 'direct', groupLabel: 'Direct attributes',
        });
        for (const a of directAttrs) {
          result.push({
            name: a.name, attribute_name: a.name, data_type: a.data_type,
            groupId: 'direct', groupLabel: 'Direct attributes',
          });
        }

        for (const g of fkGroups) {
          for (const ra of g.attrs) {
            result.push({
              name: ra.name,
              attribute_name: `${g.fkAttrName}.${ra.name}`,
              data_type: ra.data_type,
              groupId: g.id,
              groupLabel: g.label,
              refEntityType: g.refType,
            });
          }

          for (const jsonAttr of g.jsonAttrNames) {
            const jsonGroupId = `${g.id}_json_${jsonAttr}`;
            const jsonGroupLabel = `${jsonAttr} (via ${g.fkAttrName})`;
            result.push({
              name: jsonAttr,
              attribute_name: `${g.fkAttrName}.${jsonAttr}`,
              data_type: 'json',
              groupId: jsonGroupId,
              groupLabel: jsonGroupLabel,
              refEntityType: g.refType,
              wholeJsonColumn: true,
            });

            const fkDiscovered = g.discoveredJson?.find(d => d.attrName === jsonAttr);
            if (fkDiscovered) {
              for (const k of fkDiscovered.keys) {
                result.push({
                  name: jsonLeafName(jsonAttr, k.path, knownColumns),
                  attribute_name: `${g.fkAttrName}.${jsonAttr}`,
                  data_type: 'text',
                  groupId: jsonGroupId,
                  groupLabel: jsonGroupLabel,
                  refEntityType: g.refType,
                  json_path: k.path,
                });
              }
            }
          }
        }

        for (const attrName of jsonAttrNames) {
          result.push({
            name: attrName,
            attribute_name: attrName,
            data_type: 'json',
            groupId: `json_${attrName}`,
            groupLabel: `${attrName} (via ${entityType})`,
            wholeJsonColumn: true,
          });
        }

        for (const { attrName, keys } of discoveredJson) {
          for (const k of keys) {
            result.push({
              name: jsonLeafName(attrName, k.path, knownColumns),
              attribute_name: attrName,
              data_type: 'text',
              groupId: `json_${attrName}`,
              groupLabel: `${attrName} (via ${entityType})`,
              json_path: k.path,
            });
          }
        }

        if (!cancelled) setAllAttrs(result);
      } catch (err) {
        console.warn('Failed to load attributes for filter modal:', err);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, entityType, sampleEntities, extraFilters, knownColumns]);

  useEffect(() => {
    if (allAttrs.length > 0 && pendingEditRef.current) {
      const f = pendingEditRef.current;
      pendingEditRef.current = null;
      prepopulateFromFilter(f, allAttrs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAttrs]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setEditingIndex(null); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const hasSearch = attrSearch.trim().length > 0;

  const groups = useMemo<AttrGroup[]>(() => {
    const map = new Map<string, AttrGroup>();
    const search = attrSearch.toLowerCase();
    for (const attr of allAttrs) {
      if (search && !filterableAttrMatchesSearch(attr, attrSearch)) continue;
      if (!map.has(attr.groupId)) {
        map.set(attr.groupId, { id: attr.groupId, label: attr.groupLabel, items: [] });
      }
      map.get(attr.groupId)!.items.push(attr);
    }
    return Array.from(map.values());
  }, [allAttrs, attrSearch]);

  const selectAttribute = (attr: FilterableAttribute) => {
    setSelectedAttr(attr);
    const ops = attr.wholeJsonColumn
      ? WHOLE_JSON_COLUMN_OPERATORS
      : (OPERATORS[attr.data_type] || ['=']);
    setOperator(ops[0]);
    setValue('');
    setInPills([]);
    setBetweenFrom('');
    setBetweenTo('');
    setEditingIndex(null);
  };

  const prepopulateFromFilter = (f: QueryFilter, attrs: FilterableAttribute[]) => {
    const attr = findAttrMatch(f, attrs);
    if (!attr) return;
    setSelectedAttr(attr);
    const uiOperator: OperatorOption = f.operator === 'ILIKE' ? 'contains' : f.operator as OperatorOption;
    setOperator(uiOperator);
    if (f.operator === 'IS NULL' || f.operator === 'IS NOT NULL') {
      setValue('');
      setInPills([]);
    } else if (f.operator === 'IN' && Array.isArray(f.value)) {
      setInPills(f.value.map(v => String(v)));
      setValue('');
    } else if (Array.isArray(f.value)) {
      setValue(f.value.join(', '));
      setInPills([]);
    } else {
      setValue(f.value != null ? String(f.value) : '');
      setInPills([]);
    }
    setBetweenFrom('');
    setBetweenTo('');
  };

  const handleEditPill = (index: number) => {
    const f = appliedFilters[index];
    if (!f) return;
    setEditingIndex(index);
    setAttrSearch('');
    setExpanded({});

    if (allAttrs.length > 0) {
      prepopulateFromFilter(f, allAttrs);
      setOpen(true);
    } else {
      pendingEditRef.current = f;
      setSelectedAttr(null);
      setValue('');
      setInPills([]);
      setBetweenFrom('');
      setBetweenTo('');
      setOpen(true);
    }
  };

  const handleAddFilter = () => {
    if (!selectedAttr) return;

    const base: Partial<QueryFilter> = {
      attribute_name: selectedAttr.attribute_name,
    };
    if (selectedAttr.json_path) base.json_path = selectedAttr.json_path;

    const apiOperator = operator === 'contains' ? 'ILIKE' : operator;

    let newFilters: QueryFilter[];

    if (apiOperator === 'BETWEEN') {
      newFilters = [];
      if (betweenFrom) {
        newFilters.push({ ...base, operator: '>=', value: betweenFrom } as QueryFilter);
      }
      if (betweenTo) {
        newFilters.push({ ...base, operator: '<=', value: betweenTo } as QueryFilter);
      }
      if (newFilters.length === 0) return;
    } else if (apiOperator === 'IS NULL' || apiOperator === 'IS NOT NULL') {
      newFilters = [{ ...base, operator: apiOperator } as QueryFilter];
    } else if (apiOperator === 'IN') {
      if (inPills.length === 0) return;
      const parsed = selectedAttr.data_type === 'number'
        ? inPills.map(v => parseFloat(v)).filter(v => !isNaN(v))
        : inPills;
      if (parsed.length === 0) return;
      newFilters = [{ ...base, operator: apiOperator, value: parsed } as QueryFilter];
    } else {
      if (!value && value !== '0') return;
      let finalValue: QueryFilter['value'] = value;
      if (selectedAttr.data_type === 'number') {
        const num = parseFloat(value);
        if (isNaN(num)) return;
        finalValue = num;
      } else if (selectedAttr.data_type === 'boolean') {
        finalValue = value === 'true';
      }
      newFilters = [{ ...base, operator: apiOperator as QueryFilter['operator'], value: finalValue } as QueryFilter];
    }

    let updated: QueryFilter[];
    if (editingIndex !== null) {
      updated = [...appliedFilters];
      updated.splice(editingIndex, 1, ...newFilters);
      setEditingIndex(null);
    } else {
      updated = [...appliedFilters, ...newFilters];
    }
    setAppliedFilters(updated);
    onFiltersChange(updated);

    setValue('');
    setInPills([]);
    setBetweenFrom('');
    setBetweenTo('');
  };

  const removeAppliedPill = (index: number) => {
    if (editingIndex !== null) {
      if (index === editingIndex) {
        setEditingIndex(null);
      } else if (index < editingIndex) {
        setEditingIndex(editingIndex - 1);
      }
    }
    const updated = appliedFilters.filter((_, i) => i !== index);
    setAppliedFilters(updated);
    onFiltersChange(updated);
  };

  const clearFilters = () => {
    setAppliedFilters([]);
    onFiltersChange([]);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEditingIndex(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleCloseModal();
  };

  const handleOpenModal = () => {
    setAttrSearch('');
    setSelectedAttr(null);
    setValue('');
    setInPills([]);
    setBetweenFrom('');
    setBetweenTo('');
    setExpanded({});
    setEditingIndex(null);
    pendingEditRef.current = null;
    setOpen(true);
  };

  const toggleGroup = (groupId: string) => {
    setExpanded(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const operatorOptions = selectedAttr
    ? (selectedAttr.wholeJsonColumn
      ? WHOLE_JSON_COLUMN_OPERATORS
      : (OPERATORS[selectedAttr.data_type] || ['=']))
    : [];
  const needsValue = operator !== 'IS NULL' && operator !== 'IS NOT NULL';
  const isBetween = operator === 'BETWEEN';

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<(string | number)[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const fetchSuggestions = useCallback((attr: FilterableAttribute, search?: string) => {
    const targetEntityType = attr.refEntityType ?? entityType;
    const attrName = attr.attribute_name.includes('.') ? attr.attribute_name.split('.', 2)[1] : attr.attribute_name;
    setSuggestionsLoading(true);
    limsClient.getAttributeValues(targetEntityType, attrName, search || undefined, 50, attr.json_path)
      .then(result => {
        setSuggestions(result.values);
        setSuggestionsLoading(false);
      })
      .catch(() => {
        setSuggestions([]);
        setSuggestionsLoading(false);
      });
  }, [entityType]);

  const handleValueChangeWithSuggestions = useCallback((newValue: string) => {
    setValue(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (selectedAttr) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(selectedAttr, newValue);
      }, 300);
    }
  }, [selectedAttr, fetchSuggestions]);

  useEffect(() => {
    if (!showSuggestions) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  useEffect(() => {
    setSuggestions([]);
    setShowSuggestions(false);
  }, [selectedAttr]);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const renderAutocompleteInput = (placeholder: string) => {
    if (!selectedAttr) return null;
    return (
      <div className="fm-autocomplete-wrapper" ref={suggestionsRef}>
        <input
          type="text"
          className="fm-value-input"
          value={value}
          onChange={e => handleValueChangeWithSuggestions(e.target.value)}
          onFocus={() => {
            setShowSuggestions(true);
            if (suggestions.length === 0) fetchSuggestions(selectedAttr, value || undefined);
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {showSuggestions && (suggestions.length > 0 || suggestionsLoading) && (
          <div className="fm-suggestions-dropdown">
            {suggestionsLoading && suggestions.length === 0 && (
              <div className="fm-suggestion-loading">Loading...</div>
            )}
            {suggestions.map((s, i) => (
              <div
                key={i}
                className={`fm-suggestion-item${String(s) === value ? ' active' : ''}`}
                onMouseDown={e => {
                  e.preventDefault();
                  setValue(String(s));
                  setShowSuggestions(false);
                }}
              >
                {String(s)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const inInputRef = useRef<HTMLInputElement>(null);

  const addInPill = useCallback((val: string) => {
    const trimmed = val.trim();
    if (!trimmed) return;
    setInPills(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    setValue('');
    if (selectedAttr) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      fetchSuggestions(selectedAttr, undefined);
    }
  }, [selectedAttr, fetchSuggestions]);

  const removeInPill = useCallback((index: number) => {
    setInPills(prev => prev.filter((_, i) => i !== index));
  }, []);

  const renderInMultiSelect = () => {
    if (!selectedAttr) return null;
    const pillSet = new Set(inPills);
    const filteredSuggestions = suggestions.filter(s => !pillSet.has(String(s)));
    return (
      <div className="fm-autocomplete-wrapper" ref={suggestionsRef}>
        <div
          className="fm-in-multi"
          onClick={() => inInputRef.current?.focus()}
        >
          {inPills.map((pill, i) => (
            <span key={i} className="fm-in-pill">
              <span className="fm-in-pill-text">{pill}</span>
              <button
                className="fm-in-pill-remove"
                onClick={e => { e.stopPropagation(); removeInPill(i); }}
                type="button"
              >&times;</button>
            </span>
          ))}
          <input
            ref={inInputRef}
            type="text"
            className="fm-in-input"
            value={value}
            onChange={e => handleValueChangeWithSuggestions(e.target.value)}
            onFocus={() => {
              setShowSuggestions(true);
              if (suggestions.length === 0) fetchSuggestions(selectedAttr, value || undefined);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addInPill(value);
              } else if (e.key === 'Backspace' && value === '' && inPills.length > 0) {
                removeInPill(inPills.length - 1);
              }
            }}
            placeholder={inPills.length === 0 ? 'Type to search...' : ''}
            autoComplete="off"
          />
        </div>
        {showSuggestions && (filteredSuggestions.length > 0 || suggestionsLoading) && (
          <div className="fm-suggestions-dropdown">
            {suggestionsLoading && filteredSuggestions.length === 0 && (
              <div className="fm-suggestion-loading">Loading...</div>
            )}
            {filteredSuggestions.map((s, i) => (
              <div
                key={i}
                className="fm-suggestion-item"
                onMouseDown={e => {
                  e.preventDefault();
                  addInPill(String(s));
                }}
              >
                {String(s)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderValueInput = () => {
    if (!selectedAttr || !needsValue) return null;

    if (isBetween) {
      return (
        <div className="fm-between-row">
          <input
            type="datetime-local"
            className="fm-value-input fm-date-input"
            value={betweenFrom}
            onChange={e => setBetweenFrom(e.target.value)}
          />
          <span className="fm-between-label">to</span>
          <input
            type="datetime-local"
            className="fm-value-input fm-date-input"
            value={betweenTo}
            onChange={e => setBetweenTo(e.target.value)}
          />
        </div>
      );
    }

    if (selectedAttr.data_type === 'boolean') {
      return (
        <select
          className="fm-value-input"
          value={value}
          onChange={e => setValue(e.target.value)}
        >
          <option value="">Select...</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    if (selectedAttr.data_type === 'date') {
      return (
        <input
          type="datetime-local"
          className="fm-value-input fm-date-input"
          value={value}
          onChange={e => setValue(e.target.value)}
        />
      );
    }

    if (selectedAttr.data_type === 'text' || selectedAttr.data_type === 'number' || selectedAttr.json_path) {
      if (operator === 'IN') return renderInMultiSelect();
      const placeholder = selectedAttr.data_type === 'number' && !selectedAttr.json_path
        ? 'Enter number...'
        : 'Enter value...';
      return renderAutocompleteInput(placeholder);
    }

    return (
      <input
        type="text"
        className="fm-value-input"
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Enter value..."
      />
    );
  };

  return (
    <div className="entity-filters">
      {appliedFilters.length > 0 && (
        <div className="applied-pills">
          {appliedFilters.map((f, i) => (
            <span key={i} className="applied-pill" onClick={() => handleEditPill(i)} title="Click to edit">
              <span className="applied-pill-text">{formatFilterDisplay(f, allAttrs, knownColumns)}</span>
              <button className="applied-pill-remove" onClick={e => { e.stopPropagation(); removeAppliedPill(i); }} title="Remove filter">&times;</button>
            </span>
          ))}
        </div>
      )}

      <div className="filters-toolbar">
        <button onClick={handleOpenModal} className="add-filter-btn">+ Filter</button>
        {appliedFilters.length > 0 && (
          <button onClick={clearFilters} className="clear-filters-btn">Clear all</button>
        )}
      </div>

      {open && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="modal-container fm-modal" role="dialog" aria-modal="true" aria-label={editingIndex !== null ? 'Edit filter' : 'Add filter'}>
            <div className="modal-header">
              <h3>{editingIndex !== null ? 'Edit Filter' : 'Add Filter'}</h3>
              <button className="modal-close" onClick={handleCloseModal} aria-label="Close">&times;</button>
            </div>

            {appliedFilters.length > 0 && (
              <div className="fm-applied">
                {appliedFilters.map((f, i) => (
                  <span
                    key={i}
                    className={`applied-pill${editingIndex === i ? ' editing' : ''}`}
                    onClick={() => { setEditingIndex(i); prepopulateFromFilter(f, allAttrs); }}
                    title="Click to edit"
                  >
                    <span className="applied-pill-text">{formatFilterDisplay(f, allAttrs, knownColumns)}</span>
                    <button className="applied-pill-remove" onClick={e => { e.stopPropagation(); removeAppliedPill(i); }} title="Remove filter">&times;</button>
                  </span>
                ))}
              </div>
            )}

            <div className="fm-body">
              <div className="fm-left">
                <div className="fm-search-wrapper">
                  <svg className="fm-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="6.5" cy="6.5" r="5" />
                    <line x1="10" y1="10" x2="15" y2="15" />
                  </svg>
                  <input
                    type="text"
                    className="fm-search-input"
                    placeholder="Search attributes..."
                    value={attrSearch}
                    onChange={e => setAttrSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="fm-attr-list">
                  {groups.length === 0 && (
                    <div className="fm-empty">No matching attributes</div>
                  )}
                  {groups.map(group => (
                    <div key={group.id} className="modal-group">
                      <button className="modal-group-header" onClick={() => toggleGroup(group.id)}>
                        <span className={`modal-group-arrow ${(hasSearch || expanded[group.id]) ? 'expanded' : ''}`}>&#9656;</span>
                        <span className="modal-group-label">{group.label}</span>
                        <span className="modal-group-count">{group.items.length}</span>
                      </button>
                      {(hasSearch || expanded[group.id]) && (
                        <div className="fm-group-items">
                          {group.items.map(attr => {
                            const pathKey = attr.wholeJsonColumn ? '__whole__' : (attr.json_path ?? '');
                            const rowKey = `${attr.groupId}_${attr.attribute_name}_${pathKey}`;
                            const selPathKey = selectedAttr?.wholeJsonColumn ? '__whole__' : (selectedAttr?.json_path ?? '');
                            const isActive = selectedAttr && rowKey === `${selectedAttr.groupId}_${selectedAttr.attribute_name}_${selPathKey}`;
                            return (
                              <div
                                key={rowKey}
                                className={`fm-attr-item ${isActive ? 'active' : ''}`}
                                onClick={() => selectAttribute(attr)}
                              >
                                <span className="fm-attr-name">{attr.name}</span>
                                <span className="data-type-badge" data-type={attr.data_type}>{attr.data_type}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="fm-right">
                {!selectedAttr ? (
                  <div className="fm-placeholder">Select an attribute from the left to build a filter</div>
                ) : (
                  <div className="fm-form">
                    <div className="fm-form-attr">
                      <span className="fm-form-attr-name">{selectedAttr.name}</span>
                      {selectedAttr.refEntityType && <span className="fm-form-attr-via">via {selectedAttr.refEntityType}</span>}
                      <span className="data-type-badge" data-type={selectedAttr.data_type}>{selectedAttr.data_type}</span>
                    </div>

                    <label className="fm-label">Operator</label>
                    <select
                      className="fm-operator-select"
                      value={operator}
                      onChange={e => {
                        setOperator(e.target.value as OperatorOption);
                        setValue('');
                        setInPills([]);
                        setBetweenFrom('');
                        setBetweenTo('');
                      }}
                    >
                      {operatorOptions.map(op => (
                        <option key={op} value={op}>{OPERATOR_DISPLAY[op] ?? op}</option>
                      ))}
                    </select>

                    {needsValue && (
                      <>
                        <label className="fm-label">{isBetween ? 'Range' : 'Value'}</label>
                        {renderValueInput()}
                      </>
                    )}

                    <button className="fm-add-btn" onClick={handleAddFilter}>
                      {editingIndex !== null ? 'Update Filter' : 'Add Filter'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="fm-done" onClick={handleCloseModal}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
